import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, Loader2, CheckCircle, XCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { verifyEmail, resendVerification, getErrorMessage } from '../services/authService';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string>('');
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string>('');

  // Verify email on mount
  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setError('No verification token provided. Please use the link from your email.');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await verifyEmail(token);
        if (response.success) {
          setIsSuccess(true);
        } else {
          setError(response.message || 'Email verification failed.');
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    };

    verify();
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendError('');
    setResendSuccess(false);

    if (!resendEmail.trim()) {
      setResendError('Please enter your email address');
      return;
    }

    setIsResending(true);

    try {
      const response = await resendVerification({ email: resendEmail });
      if (response.success) {
        setResendSuccess(true);
      } else {
        setResendError(response.message || 'Failed to resend verification email.');
      }
    } catch (err) {
      setResendError(getErrorMessage(err));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation variant="dark" />

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center px-4 py-8 pt-32" style={{minHeight: 'calc(100vh - 120px)'}}>
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-black mb-4 text-white">
              Email Verification
            </h1>
            <p className="text-lg text-slate-400">
              {isVerifying ? 'Verifying your email...' :
               isSuccess ? 'Your email is verified!' :
               'Verification failed'}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            {isVerifying ? (
              // Loading State
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 text-slate-400 animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Verifying your email address...</p>
              </div>
            ) : isSuccess ? (
              // Success State
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-white">Email Verified!</h2>
                <p className="text-slate-400 mb-6">
                  Your email has been successfully verified. You can now sign in to your account.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 rounded-xl transition-all font-bold text-white"
                >
                  Sign In to Your Account
                </Link>
              </div>
            ) : (
              // Error State with Resend Option
              <div className="py-4">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <h2 className="text-xl font-bold mb-2 text-white">Verification Failed</h2>
                  <p className="text-slate-400">{error}</p>
                </div>

                {/* Resend Verification Form */}
                <div className="border-t border-white/10 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-4 text-center">
                    Need a new verification link?
                  </h3>

                  {resendSuccess ? (
                    <div className="text-center p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                      <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <p className="text-green-200">
                        A new verification email has been sent to <span className="font-medium">{resendEmail}</span>
                      </p>
                      <p className="text-sm text-slate-400 mt-2">Check your inbox and spam folder.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleResend} className="space-y-4">
                      {resendError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start space-x-2">
                          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-200">{resendError}</p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2">
                          <Mail className="w-4 h-4 inline mr-2" />
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={resendEmail}
                          onChange={(e) => setResendEmail(e.target.value)}
                          disabled={isResending}
                          className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Enter your email"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isResending}
                        className="w-full px-6 py-3 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 rounded-xl transition-all font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isResending ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <span>Resend Verification Email</span>
                        )}
                      </button>
                    </form>
                  )}
                </div>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Login</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VerifyEmail;
